import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ZOOM_MIN, ZOOM_MAX, normalizeFraming, isDefaultFraming, CROP_WARNING_THRESHOLD } from './framing';

// PANNEAU "PHOTO SELECTIONNEE" (phase 4b) : le panneau de gauche s'adapte a ce
// qu'on a selectionne sur la page. Meme reglages que la barre flottante (Remplir
// / Entiere, Remplacer, Retirer), plus ceux qui gagnent a avoir de la place :
// un curseur de zoom (utile au clavier et au tactile), la legende, et l'etat de
// la photo (coupe, definition). Aucun enregistrement ici : tout passe par les
// memes actions que sur la page (historique, sauvegarde, rendu identiques).
function AtelierPhotoPanel({
  item, framing, croppedPct, isCover, spread, caption, onBack, onFit, onZoom, onReset,
  onCaption, onReplace, onRemove
}) {
  const { t } = useTranslation('atelier');
  const current = normalizeFraming(framing);
  const [captionDraft, setCaptionDraft] = useState(caption || '');
  useEffect(() => { setCaptionDraft(caption || ''); }, [caption, item && item.id]);

  const url = item && (item.metadata?.previewUrl || item.metadata?.thumbnailUrl || item.url);
  const cropped = Math.round((croppedPct || 0) * 100);

  return (
    <div className="atelier-layout-panel atelier-photo-panel">
      <div className="atelier-layout-panel-head">
        <button type="button" className="atelier-photo-panel-back" onClick={onBack}>← {t('selection.back')}</button>
        <span className="atelier-layout-panel-title">{isCover ? t('selection.coverPhoto') : t('selection.photo')}</span>
      </div>

      {spread && (
        <details className="atelier-photo-panel-spread">
          <summary>{t('spreadInfo.title', { ns: 'common' })}</summary>
          <p>{t('spreadInfo.advice', { ns: 'common' })}</p>
          <p>{t('spreadInfo.continuity', { ns: 'common' })}</p>
        </details>
      )}

      {url && <img className="atelier-photo-panel-img" src={url} alt="" draggable={false} />}

      <div className="atelier-photo-panel-group">
        <span className="atelier-photo-panel-label">{t('selection.framing')}</span>
        <div className="atelier-framing-seg is-panel" role="group" aria-label={t('framing.fitGroup')}>
          <button type="button" aria-pressed={current.fit === 'cover'} onClick={() => onFit('cover')}>{t('framing.fill')}</button>
          <button type="button" aria-pressed={current.fit === 'contain'} onClick={() => onFit('contain')}>{t('framing.whole')}</button>
        </div>
        {croppedPct > CROP_WARNING_THRESHOLD && current.fit === 'cover' && (
          <p className="atelier-photo-panel-warn">{t('framing.cropWarning', { percent: cropped })}</p>
        )}
      </div>

      <div className="atelier-photo-panel-group">
        <label className="atelier-photo-panel-label" htmlFor="atelier-zoom-range">
          {t('selection.zoom')} <b>{current.zoom.toFixed(1)}×</b>
        </label>
        <input
          id="atelier-zoom-range"
          type="range"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step="0.05"
          value={current.zoom}
          disabled={current.fit !== 'cover'}
          onChange={(event) => onZoom(Number(event.target.value))}
        />
        <p className="atelier-photo-panel-tip">{current.fit === 'cover' ? t('selection.zoomTip') : t('selection.zoomDisabled')}</p>
        <button type="button" className="atelier-photo-panel-btn" onClick={onReset} disabled={isDefaultFraming(current)}>
          {t('selection.reset')}
        </button>
      </div>

      {!isCover && onCaption && (
        <div className="atelier-photo-panel-group">
          <label className="atelier-photo-panel-label" htmlFor="atelier-caption-field">{t('selection.caption')}</label>
          <input
            id="atelier-caption-field"
            type="text"
            maxLength={140}
            value={captionDraft}
            placeholder={t('pageOverlay.captionPlaceholder')}
            onChange={(event) => setCaptionDraft(event.target.value)}
            onBlur={() => { if (captionDraft !== (caption || '')) onCaption(captionDraft); }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); onCaption(captionDraft); }
              if (event.key === 'Escape') { event.stopPropagation(); setCaptionDraft(caption || ''); }
            }}
          />
        </div>
      )}

      <div className="atelier-photo-panel-actions">
        {onReplace && <button type="button" className="atelier-photo-panel-btn" onClick={onReplace}>{t('framing.replace')}</button>}
        {onRemove && <button type="button" className="atelier-photo-panel-btn is-danger" onClick={onRemove}>{t('framing.remove')}</button>}
      </div>
    </div>
  );
}

export default AtelierPhotoPanel;

// Panneau "texte" : un texte est en cours d'edition sur la page. L'editeur
// porte deja ses propres reglages (role, style) ; ce panneau dit seulement
// comment en sortir.
export function AtelierTextPanel({ onBack }) {
  const { t } = useTranslation('atelier');
  return (
    <div className="atelier-layout-panel atelier-photo-panel">
      <div className="atelier-layout-panel-head">
        <button type="button" className="atelier-photo-panel-back" onClick={onBack}>← {t('selection.back')}</button>
        <span className="atelier-layout-panel-title">{t('selection.text')}</span>
      </div>
      <ul className="atelier-text-panel-tips">
        <li>{t('selection.textTipWrite')}</li>
        <li>{t('selection.textTipEscape')}</li>
        <li>{t('selection.textTipSave')}</li>
      </ul>
    </div>
  );
}
